// features/workstations/qqq/prediction/f15-panel.js
// ═══════════════════════════════════════════════════════════
// F15 LOW SECONDARY SIGNAL — earlier event-driven downside entry
//
// Polls /api/qqq/f15-secondary-signal every 60s when watching.
// Fires when agent (10:30 ET) says DOWN ≥0.55 AND a 5-min bar
// closes below F15 Low. Backtested: 61% win rate · $116/trade real
// over 33 trades / 2 years (highest WR in our entire backtest).
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from 'react';

function todayISO() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

const STATE_META = {
  WAITING_F15:     { label: 'WAITING F15',     color: '#888',     desc: 'Market just opened, F15 forming' },
  F15_LOCKED:      { label: 'F15 LOCKED',      color: '#c8a96e',  desc: 'F15 H/L known, waiting for 10:30 agent' },
  WATCHING:        { label: 'WATCHING',        color: '#7a9e82',  desc: 'Agent says DOWN — watching for F15L break' },
  NO_SIGNAL_TODAY: { label: 'NO SIGNAL',       color: '#888',     desc: 'Agent did not agree — no F15L trade today' },
  FIRED:           { label: '🔻 FIRED',         color: '#c46050',  desc: 'F15L broke after agent down — trade signal active' },
  EXPIRED:         { label: 'EXPIRED',         color: '#666',     desc: 'Day ended without signal firing' },
};

export default function F15Panel({ collapsed = false }) {
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(!collapsed);
  const pollTimer = useRef(null);
  const lastFetchedAt = useRef(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/qqq/f15-secondary-signal?date=${date}`);
      const json = await res.json();
      if (json.error && !json.state) { setError(json.message || json.error); setData(json); }
      else { setData(json); lastFetchedAt.current = new Date(); }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [date]);

  // Auto-run on mount / date change
  useEffect(() => { if (open) run(); }, [open, date, run]);

  // Auto-poll every 60s when state warrants (WATCHING, F15_LOCKED, or waiting)
  useEffect(() => {
    if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
    if (!open || !data || date !== todayISO()) return;
    const stopStates = ['FIRED', 'NO_SIGNAL_TODAY', 'EXPIRED'];
    if (stopStates.includes(data.state)) return;  // freeze polling once decided
    pollTimer.current = setInterval(() => run(), 60_000);
    return () => { if (pollTimer.current) clearInterval(pollTimer.current); };
  }, [open, data, date, run]);

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="qqq-f15-toggle">
        ▶ Open F15 Low Secondary Signal
      </button>
    );
  }

  const meta = data ? (STATE_META[data.state] || STATE_META.WAITING_F15) : STATE_META.WAITING_F15;

  return (
    <div className="qqq-f15">
      <div className="qqq-f15__header">
        <div>
          <h3>F15 Low Secondary Signal</h3>
          <div className="qqq-f15__sub">Agent-down + F15L break · 61% WR · $116/trade real · ~16/yr</div>
        </div>
        <div className="qqq-f15__controls">
          <input
            type="date" value={date}
            onChange={(e) => setDate(e.target.value)}
            className="qqq-f15__date"
          />
          <button onClick={run} disabled={loading} className="qqq-f15__run">
            {loading ? 'Polling…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* State banner */}
      {data && (
        <div className="qqq-f15__state" style={{ borderColor: `${meta.color}66`, background: `${meta.color}11` }}>
          <div className="qqq-f15__state-row">
            <span className="qqq-f15__state-badge" style={{ background: `${meta.color}22`, color: meta.color }}>
              {meta.label}
            </span>
            <span className="qqq-f15__state-time">
              {data.current_time_et}
              {pollTimer.current && date === todayISO() && <span className="qqq-f15__polling"> · polling 60s</span>}
            </span>
          </div>
          <div className="qqq-f15__state-msg">{data.message || meta.desc}</div>
        </div>
      )}

      {error && <div className="qqq-f15__error">⚠ {error}</div>}

      {/* Body */}
      {data && !data.error && (
        <div className="qqq-f15__body">
          {/* Level + price */}
          {data.f15_low != null && (
            <div className="qqq-f15__section">
              <div className="qqq-f15__section-title">F15 Levels (9:30–9:45 ET)</div>
              <div className="qqq-f15__grid">
                <div className="qqq-f15__cell">
                  <span className="qqq-f15__cell-label">F15 High</span>
                  <span className="qqq-f15__cell-value">${data.f15_high?.toFixed(2)}</span>
                </div>
                <div className="qqq-f15__cell qqq-f15__cell--key">
                  <span className="qqq-f15__cell-label">F15 Low ⭐</span>
                  <span className="qqq-f15__cell-value">${data.f15_low?.toFixed(2)}</span>
                </div>
                <div className="qqq-f15__cell">
                  <span className="qqq-f15__cell-label">Range</span>
                  <span className="qqq-f15__cell-value">${data.f15_range?.toFixed(2)}</span>
                </div>
                <div className="qqq-f15__cell">
                  <span className="qqq-f15__cell-label">QQQ Now</span>
                  <span className="qqq-f15__cell-value">${data.current_price?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Agent status */}
          <div className="qqq-f15__section">
            <div className="qqq-f15__section-title">10:30 ET Agent · DOWN-direction gate</div>
            {data.agent?.ready ? (
              <div className="qqq-f15__agent">
                <div className="qqq-f15__agent-row">
                  <span className={`qqq-f15__agent-call qqq-f15__agent-call--${data.agent.top1}`}>
                    {data.agent.top1.toUpperCase()}
                  </span>
                  <span className="qqq-f15__agent-conf">
                    Conf: <strong>{(data.agent.conf * 100).toFixed(1)}%</strong>
                    &nbsp;·&nbsp;
                    up={(data.agent.probs.up * 100).toFixed(0)}%,
                    down={(data.agent.probs.down * 100).toFixed(0)}%,
                    chop={(data.agent.probs.chop * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="qqq-f15__agent-filter">
                  Filter requires: <code>down + conf ≥ 0.55</code> &nbsp;·&nbsp;
                  Passes: <strong style={{ color: data.agent.passes_filter ? '#7a9e82' : '#c46050' }}>
                    {data.agent.passes_filter ? '✓ YES' : '✗ NO'}
                  </strong>
                </div>
              </div>
            ) : (
              <div className="qqq-f15__waiting">Waiting for 10:30 ET (need 4 cluster windows complete)</div>
            )}
          </div>

          {/* Fire conditions checklist */}
          <div className="qqq-f15__section">
            <div className="qqq-f15__section-title">Fire conditions (all 3 required)</div>
            <ul className="qqq-f15__conditions">
              <li className={data.fire_conditions?.f15_locked ? 'met' : ''}>
                {data.fire_conditions?.f15_locked ? '✓' : '◯'} F15 has closed (9:45 ET)
              </li>
              <li className={data.fire_conditions?.agent_says_down_with_conf ? 'met' : ''}>
                {data.fire_conditions?.agent_says_down_with_conf ? '✓' : '◯'} Agent says DOWN with conf ≥ 0.55
              </li>
              <li className={data.fire_conditions?.f15_low_broken ? 'met' : ''}>
                {data.fire_conditions?.f15_low_broken ? '✓' : '◯'} A bar closed below F15 Low after 9:45 ET
              </li>
            </ul>
          </div>

          {/* Trade card (only when FIRED) */}
          {data.trade_card && (
            <div className="qqq-f15__decision">
              <div className="qqq-f15__decision-header">
                <span className="qqq-f15__decision-badge">🔻 PUT SPREAD — short bias</span>
                <span className="qqq-f15__decision-agent">Agent conf {(data.trade_card.agent_conf * 100).toFixed(0)}%</span>
              </div>
              <div className="qqq-f15__trade">
                <div className="qqq-f15__row">
                  <span className="qqq-f15__label">Vehicle</span>
                  <span className="qqq-f15__value">{data.trade_card.vehicle}</span>
                </div>
                <div className="qqq-f15__row">
                  <span className="qqq-f15__label">Entry</span>
                  <span className="qqq-f15__value">{data.trade_card.entry_time_et} ET @ QQQ ${data.trade_card.entry_price}</span>
                </div>
                <div className="qqq-f15__row">
                  <span className="qqq-f15__label">Buy put</span>
                  <span className="qqq-f15__value">strike ${data.trade_card.strike} (ATM)</span>
                </div>
                <div className="qqq-f15__row">
                  <span className="qqq-f15__label">Sell put</span>
                  <span className="qqq-f15__value">strike ${data.trade_card.otm_strike} (-${data.trade_card.spread_width} OTM)</span>
                </div>
                <div className="qqq-f15__row qqq-f15__row--accent">
                  <span className="qqq-f15__label">Profit-take</span>
                  <span className="qqq-f15__value">QQQ ${data.trade_card.profit_take_price}</span>
                </div>
              </div>
              <div className="qqq-f15__exits">
                <div className="qqq-f15__section-title">Exit rules</div>
                <ol>
                  <li><strong>Profit-take (X1)</strong>: {data.trade_card.exit_rules.x1_profit_take}</li>
                  <li><strong>Stop-loss (X2)</strong>: {data.trade_card.exit_rules.x2_stop}</li>
                  <li><strong>Level invalidation</strong>: {data.trade_card.exit_rules.level_invalidation}</li>
                  <li><strong>Default</strong>: {data.trade_card.exit_rules.default}</li>
                </ol>
              </div>
            </div>
          )}

          <div className="qqq-f15__perf">
            <strong>Backtested</strong> · {data.backtested_performance.n_trades_2yr} trades over 2 years
            · {(data.backtested_performance.win_rate * 100).toFixed(0)}% win rate
            · ${data.backtested_performance.per_trade_real}/trade after slippage
            <div className="qqq-f15__perf-note">{data.backtested_performance.note}</div>
          </div>
        </div>
      )}

      <style jsx>{`
        .qqq-f15-toggle {
          margin: 16px 0; padding: 8px 14px;
          background: #111118; color: #c46050;
          border: 1px solid #c4605066; border-radius: 6px;
          font-size: 13px; cursor: pointer;
        }
        .qqq-f15 {
          margin: 16px 0; padding: 16px;
          background: #07070a; border: 1px solid #272736; border-radius: 8px;
          color: #ccc; font-size: 13px; line-height: 1.5;
        }
        .qqq-f15__header {
          display: flex; align-items: flex-start; justify-content: space-between;
          margin-bottom: 16px; padding-bottom: 12px;
          border-bottom: 1px solid #272736;
        }
        .qqq-f15__header h3 { margin: 0 0 4px; color: #c46050; font-size: 16px; font-weight: 600; }
        .qqq-f15__sub { color: #888; font-size: 11px; }
        .qqq-f15__controls { display: flex; gap: 8px; }
        .qqq-f15__date {
          background: #171723; color: #ccc; border: 1px solid #3a3a52;
          padding: 4px 8px; border-radius: 4px; font-size: 12px;
        }
        .qqq-f15__run {
          background: #c46050; color: #07070a; border: none;
          padding: 4px 14px; border-radius: 4px; cursor: pointer;
          font-size: 12px; font-weight: 600;
        }
        .qqq-f15__run:disabled { opacity: 0.5; cursor: wait; }
        .qqq-f15__state {
          padding: 12px 14px; border: 1px solid; border-radius: 6px; margin-bottom: 14px;
        }
        .qqq-f15__state-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
        .qqq-f15__state-badge {
          padding: 4px 10px; border-radius: 4px; font-size: 12px; font-weight: 700; letter-spacing: 0.05em;
        }
        .qqq-f15__state-time { font-size: 11px; color: #888; font-family: ui-monospace, monospace; }
        .qqq-f15__polling { color: #7a9e82; }
        .qqq-f15__state-msg { font-size: 12px; color: #ccc; line-height: 1.4; }
        .qqq-f15__error {
          padding: 10px 14px; background: #c4605022; border: 1px solid #c4605066;
          border-radius: 4px; color: #c46050; font-size: 12px; margin-bottom: 12px;
        }
        .qqq-f15__body > div { margin-bottom: 14px; }
        .qqq-f15__section {
          padding: 12px 14px; background: #111118; border: 1px solid #272736; border-radius: 4px;
        }
        .qqq-f15__section-title {
          font-size: 10px; color: #888; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px;
        }
        .qqq-f15__grid {
          display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px;
        }
        .qqq-f15__cell {
          padding: 8px 10px; background: #07070a; border-radius: 4px;
          display: flex; flex-direction: column; gap: 4px;
        }
        .qqq-f15__cell--key { background: #c4605011; border: 1px solid #c4605044; }
        .qqq-f15__cell-label { font-size: 10px; color: #888; text-transform: uppercase; letter-spacing: 0.05em; }
        .qqq-f15__cell-value { font-family: ui-monospace, monospace; font-size: 14px; color: #ccc; font-weight: 600; }
        .qqq-f15__agent { font-size: 12px; }
        .qqq-f15__agent-row { display: flex; align-items: center; gap: 12px; margin-bottom: 6px; }
        .qqq-f15__agent-call {
          padding: 4px 10px; border-radius: 4px; font-size: 12px; font-weight: 700; letter-spacing: 0.05em;
        }
        .qqq-f15__agent-call--up { background: #7a9e8222; color: #7a9e82; }
        .qqq-f15__agent-call--down { background: #c4605022; color: #c46050; }
        .qqq-f15__agent-call--chop { background: #9b8fc422; color: #9b8fc4; }
        .qqq-f15__agent-conf { color: #aaa; font-family: ui-monospace, monospace; font-size: 11px; }
        .qqq-f15__agent-filter { font-size: 11px; color: #888; }
        .qqq-f15__agent-filter code { color: #c8a96e; background: #07070a; padding: 1px 6px; border-radius: 2px; }
        .qqq-f15__conditions {
          list-style: none; padding: 0; margin: 0; font-family: ui-monospace, monospace; font-size: 12px;
        }
        .qqq-f15__conditions li { color: #888; padding: 3px 0; }
        .qqq-f15__conditions li.met { color: #7a9e82; }
        .qqq-f15__waiting { color: #888; font-style: italic; font-size: 12px; }
        .qqq-f15__decision {
          padding: 16px; background: #c4605008; border: 2px solid #c4605066; border-radius: 6px;
        }
        .qqq-f15__decision-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
        .qqq-f15__decision-badge {
          padding: 6px 12px; background: #c46050; color: #07070a; border-radius: 4px;
          font-size: 13px; font-weight: 700; letter-spacing: 0.05em;
        }
        .qqq-f15__decision-agent { color: #c46050; font-size: 12px; font-family: ui-monospace, monospace; }
        .qqq-f15__trade { display: flex; flex-direction: column; gap: 4px; margin-bottom: 14px; }
        .qqq-f15__row {
          display: grid; grid-template-columns: 130px 1fr;
          padding: 6px 10px; background: #07070a; border-radius: 4px;
          font-family: ui-monospace, monospace; font-size: 12px;
        }
        .qqq-f15__row--accent { background: #c4605011; border: 1px solid #c4605044; }
        .qqq-f15__label { color: #888; }
        .qqq-f15__value { color: #ccc; font-weight: 600; }
        .qqq-f15__exits ol { padding-left: 22px; margin: 8px 0 0; }
        .qqq-f15__exits li { font-size: 11px; color: #aaa; margin: 4px 0; line-height: 1.5; }
        .qqq-f15__exits strong { color: #c46050; }
        .qqq-f15__perf {
          padding: 10px 14px; background: #07070a; border: 1px solid #1e1e2a;
          border-radius: 4px; font-size: 11px; color: #888;
        }
        .qqq-f15__perf strong { color: #c8a96e; }
        .qqq-f15__perf-note { margin-top: 4px; opacity: 0.7; font-style: italic; }
      `}</style>
    </div>
  );
}
